import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "./supabaseClient";
import Navbar from "./components/Navbar";
import { normalizeTech, normalizeJob, hasAnyCertExpired, calcBestMatch, calcJobMatch, techHasCert } from "./utils/helpers";
import TechCard from "./components/TechCard";
import { CERT_COLORS, ALL_CERT_OPTIONS, UXO_ROLE_OPTIONS } from "./utils/constants";

const JOB_STATUS_META = {
  published:       { bg: '#0a2a1a', color: '#4ade80',  label: 'Published' },
  pending_payment: { bg: '#2a1e08', color: '#fbbf24',  label: 'Pending Payment' },
  expired:         { bg: '#2a0a0a', color: '#f87171',  label: 'Expired' },
};

const APP_STATUS_LABELS  = {
  submitted:    'Submitted',
  under_review: 'Under Review',
  hired:        'Hired',
  rejected:     'Rejected',
};
const APP_STATUS_COLORS = {
  submitted:    '#9a9490',
  under_review: '#fbbf24',
  hired:        '#4ade80',
  rejected:     '#f87171',
};

function StatusBadge({ status, meta }) {
  const m = meta[status] || { bg: '#1a1c1e', color: '#9a9490', label: status };
  return (
    <span style={{
      background: m.bg, color: m.color, fontSize: 11, padding: '3px 9px',
      borderRadius: 12, fontWeight: 'bold', whiteSpace: 'nowrap',
      border: `1px solid ${m.color}33`,
    }}>
      {"\u25CF"} {m.label}
    </span>
  );
}

export default function EmployerDashboard({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab]                   = useState(location.state?.openTab || 'posts');
  const [jobPostSuccess, setJobPostSuccess] = useState(location.state?.jobPostSuccess || false);

  // Clear the navigation state so it doesn't persist on refresh
  useEffect(() => {
    if (location.state?.jobPostSuccess || location.state?.openTab) {
      navigate(location.pathname, { replace: true, state: {} });
      if (location.state?.jobPostSuccess) setTimeout(() => setJobPostSuccess(false), 5000);
    }
  }, []); // eslint-disable-line
  const [posts, setPosts]               = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState(null);
  const [applications, setApplications] = useState({});
  const [appsLoading, setAppsLoading]   = useState({});
  const [contacts, setContacts]         = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm]     = useState(null);
  const [techs, setTechs]              = useState([]);
  const [techsLoading, setTechsLoading] = useState(false);
  const [isPaid, setIsPaid]            = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const [activeTech, setActiveTech]    = useState(null);

  // ── Edit job state ──
  const [editingPost, setEditingPost] = useState(null); // full post object being edited
  const [editForm, setEditForm] = useState(null);
  const [editStep, setEditStep] = useState(1);
  const [maxEditStep, setMaxEditStep] = useState(1);
  const [editSaving, setEditSaving] = useState(false);
  const [editSuccess, setEditSuccess] = useState(false);
  const [editTitleIsOther, setEditTitleIsOther] = useState(false);
  const [editStep1Errors, setEditStep1Errors] = useState({});
  const [editStep2Error, setEditStep2Error] = useState('');

  // ── Invite to Apply state ──
  const [inviteTarget, setInviteTarget] = useState(null); // { techId, techName, techRolePref, techRoles }
  const [inviteJobId, setInviteJobId] = useState(null);
  const [inviteWarning, setInviteWarning] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(new Set()); // Set of "techId-jobId" keys
  const [existingInvites, setExistingInvites] = useState(new Set()); // Set of "techId-jobId" keys
  const [employerCompanyName, setEmployerCompanyName] = useState('');

  const [acceptUnlockPrompt, setAcceptUnlockPrompt] = useState(null); // { techId, techName }
  const [techApplications, setTechApplications] = useState({}); // { techId: Set of jobIds }

  // ── Proxy message state (for replying to applicants without unlock) ──
  const [replyingTo, setReplyingTo] = useState(null); // app id being replied to
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replySent, setReplySent] = useState(new Set());

  // ── Unlock system state ──
  const [employerProfileId, setEmployerProfileId] = useState(null);
  const [unlockedTechIds, setUnlockedTechIds] = useState(new Set());
  const [unlockBalance, setUnlockBalance] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [overageConfirmTech, setOverageConfirmTech] = useState(null);
  const [myContacts, setMyContacts] = useState([]);
  const [myContactsLoading, setMyContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [techSort, setTechSort] = useState('bestFit');
  const [hideExpired, setHideExpired] = useState(false);
  const [employerJobs, setEmployerJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('all');
  const [onlyMatching, setOnlyMatching] = useState(false);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [onlyLogbook, setOnlyLogbook] = useState(false);
  const [verifiedDisclaimerDismissed, setVerifiedDisclaimerDismissed] = useState(() => localStorage.getItem('uxo_verified_disclaimer_dismissed') === '1');
  const [contactMsgTo, setContactMsgTo] = useState(null); // unlock id being messaged
  // ── Employer conversations state (messages table) ──
  const [convos, setConvos] = useState([]);
  const [convosLoading, setConvosLoading] = useState(false);
  const [openConvo, setOpenConvo] = useState(null); // tech_profile_id of open thread
  const [convoReplyText, setConvoReplyText] = useState('');
  const [convoReplySending, setConvoReplySending] = useState(false);
  const [convoSearch, setConvoSearch] = useState('');
  const [empUnreadCount, setEmpUnreadCount] = useState(0);
  const [unreadAppsCount, setUnreadAppsCount] = useState(0);
  const [contactMsgText, setContactMsgText] = useState('');
  const [contactMsgSending, setContactMsgSending] = useState(false);
  const [contactMsgSent, setContactMsgSent] = useState(new Set());

  // Fetch account status + employer profile id
  useEffect(() => {
    setAccountLoading(true);
    const loadProfile = async () => {
      const [{ data: acctData }, { data: epData }] = await Promise.all([
        supabase.from('user_accounts').select('is_paid_employer').eq('user_id', user.id).maybeSingle(),
        supabase.from('employer_profiles').select('id, company_name').eq('user_id', user.id).maybeSingle(),
      ]);
      setIsPaid(acctData?.is_paid_employer || false);

      if (epData?.id) {
        setEmployerProfileId(epData.id);
        setEmployerCompanyName(epData.company_name || '');
      } else if (user.email) {
        // Fallback: match by contact_email (handles profiles created before user_id was set)
        const { data: epByEmail } = await supabase.from('employer_profiles')
          .select('id, company_name, user_id')
          .eq('contact_email', user.email)
          .maybeSingle();
        if (epByEmail?.id) {
          setEmployerProfileId(epByEmail.id);
          setEmployerCompanyName(epByEmail.company_name || '');
          // Backfill user_id if it doesn't match
          if (epByEmail.user_id !== user.id) {
            supabase.from('employer_profiles')
              .update({ user_id: user.id })
              .eq('id', epByEmail.id)
              .then(({ error }) => { if (error) console.error('user_id backfill failed:', error); });
          }
        }
      }
      setAccountLoading(false);
    };
    loadProfile();
  }, [user.id, user.email]);

  // Fetch unlocked techs + balance when employer profile is known
  useEffect(() => {
    if (!employerProfileId || !isPaid) return;

    // Fetch unlocked tech ids
    supabase.from('contact_unlocks').select('tech_profile_id')
      .eq('employer_id', employerProfileId)
      .then(({ data }) => {
        setUnlockedTechIds(new Set((data || []).map(r => r.tech_profile_id).filter(Boolean)));
      });

    // Fetch existing invitations (exclude declined — employer can re-invite after a decline)
    supabase.from('invitations').select('tech_profile_id, job_post_id, status')
      .eq('employer_profile_id', employerProfileId)
      .neq('status', 'declined')
      .then(({ data }) => {
        setExistingInvites(new Set((data || []).map(r => `${r.tech_profile_id}-${r.job_post_id}`)));
      });

    // Fetch which techs have already applied to this employer's jobs
    supabase.from('applications').select('tech_profile_id, job_id, job_posts!inner(user_id)')
      .eq('job_posts.user_id', user.id)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(r => {
          if (!r.tech_profile_id) return;
          if (!map[r.tech_profile_id]) map[r.tech_profile_id] = new Set();
          map[r.tech_profile_id].add(r.job_id);
        });
        setTechApplications(map);
      });

    // Fetch or initialize balance, then check for monthly reset
    supabase.from('unlock_balance').select('*')
      .eq('employer_id', employerProfileId).single()
      .then(({ data, error }) => {
        if (error || !data) {
          // Initialize balance for this employer (upsert to handle concurrent tab race)
          supabase.from('unlock_balance').upsert({
            employer_id: employerProfileId,
            monthly_allowance: 10,
            current_balance: 10,
            last_reset_at: new Date().toISOString(),
          }, { onConflict: 'employer_id' })
          .select().single().then(({ data: newBal }) => {
            if (newBal) setUnlockBalance(newBal);
          });
        } else {
          // Check if monthly reset is needed (30+ days since last reset)
          const lastReset = new Date(data.last_reset_at);
          const daysSinceReset = (Date.now() - lastReset.getTime()) / (1000 * 60 * 60 * 24);
          if (daysSinceReset >= 30) {
            const missedCycles = Math.floor(daysSinceReset / 30);
            let newBalance;
            if (missedCycles === 1) {
              // Single cycle: rollover unused balance, capped at 2x allowance
              const rollover = Math.min(data.current_balance, data.monthly_allowance);
              newBalance = Math.min(data.monthly_allowance + rollover, data.monthly_allowance * 2);
            } else {
              // Multiple missed cycles: no rollover from stale balance,
              // employer gets full monthly allowance (rollover expires after 1 cycle)
              newBalance = data.monthly_allowance;
            }
            supabase.from('unlock_balance').update({
              current_balance: newBalance,
              last_reset_at: new Date().toISOString(),
            }).eq('id', data.id).select().single().then(({ data: updated }) => {
              setUnlockBalance(updated);
            });
            // Reset overage count
            supabase.from('employer_profiles').update({ unlock_overage_count: 0 })
              .eq('id', employerProfileId);
          } else {
            setUnlockBalance(data);
          }
        }
      });
  }, [employerProfileId, isPaid]);

  // ── Unlock a tech profile ──
  const handleUnlock = async (techProfileId) => {
    if (!techProfileId || !employerProfileId || !isPaid) return;
    if (unlockedTechIds.has(techProfileId)) return;

    setUnlockLoading(true);

    // Check balance
    if (unlockBalance && unlockBalance.current_balance <= 0) {
      // Show overage confirmation
      setOverageConfirmTech(techProfileId);
      setUnlockLoading(false);
      return;
    }

    await performUnlock(techProfileId, false);
  };

  // One RPC now does the insert, the profile snapshot and the balance move in
  // a single transaction (migration 078). The snapshot has to be written
  // server-side to be worth anything, and the client can no longer decide
  // whether a given unlock counts as an overage — the server decides that
  // from the actual balance.
  const performUnlock = async (techProfileId, isOverage) => {
    setUnlockLoading(true);
    try {
      const { data, error } = await supabase.rpc('unlock_tech_contact', {
        p_employer_id: employerProfileId,
        p_tech_profile_id: techProfileId,
        p_accept_overage: !!isOverage,
      });

      if (error) {
        console.error('[performUnlock] rpc error:', error);
        // The server refused because the balance is gone and no overage was
        // authorised. Surface the confirm dialog instead of failing silently.
        if ((error.message || '').includes('insufficient_balance')) {
          setOverageConfirmTech(techProfileId);
        }
        setUnlockLoading(false);
        return;
      }

      if (data?.was_overage) {
        // Report overage to Stripe metered billing (fire and forget)
        supabase.functions.invoke('report-overage-usage', {
          body: { employer_profile_id: employerProfileId },
        }).catch(() => {});
      } else if (data?.new_balance != null) {
        setUnlockBalance(prev => prev ? { ...prev, current_balance: data.new_balance } : prev);
      }

      // Update local state
      setUnlockedTechIds(prev => new Set([...prev, techProfileId]));
      setOverageConfirmTech(null);

      // Fire notification edge function (fire and forget)
      supabase.functions.invoke('send-unlock-notification', {
        body: { tech_profile_id: techProfileId },
      }).catch(() => {});
    } finally {
      setUnlockLoading(false);
    }
  };

  const confirmOverage = () => {
    if (overageConfirmTech) performUnlock(overageConfirmTech, true);
  };

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    const { data } = await supabase
      .from('job_posts')
      .select('id, title, company, location, type, status, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setPosts(data || []);
    setPostsLoading(false);
  }, [user.id]);

  const fetchApplications = async (postId) => {
    setAppsLoading(p => ({ ...p, [postId]: true }));
    const { data } = await supabase
      .from('applications')
      .select('id, applicant_name, applicant_email, message, status, created_at, tech_profile_id, is_read, tech_profiles(email, phone, location)')
      .eq('job_id', postId)
      .order('created_at', { ascending: false });
    setApplications(p => ({ ...p, [postId]: data || [] }));
    setAppsLoading(p => ({ ...p, [postId]: false }));

    // Mark unread applications as read for this job
    const unread = (data || []).filter(a => !a.is_read);
    if (unread.length > 0) {
      await supabase.from('applications').update({ is_read: true })
        .eq('job_id', postId).eq('is_read', false);
      fetchUnreadAppsCount();
    }
  };

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    const { data } = await supabase
      .from('contacts')
      .select('id, message, created_at, company, tech_profiles(name, location)')
      .eq('employer_email', user.email)
      .order('created_at', { ascending: false });
    setContacts(data || []);
    setContactsLoading(false);
  }, [user.email]);

  // Fetch conversations from messages table
  const fetchConvos = useCallback(async () => {
    if (!employerProfileId) return;
    setConvosLoading(true);
    const { data } = await supabase
      .from('messages')
      .select('id, tech_profile_id, sender_type, message, is_read, created_at, tech_profiles(id, name, location, dod_certs)')
      .eq('employer_profile_id', employerProfileId)
      .order('created_at', { ascending: false });
    setConvos(data || []);
    setEmpUnreadCount((data || []).filter(m => m.sender_type === 'tech' && !m.is_read).length);
    setConvosLoading(false);
  }, [employerProfileId]);

  const fetchTechs = useCallback(async () => {
    setTechsLoading(true);
    const { data } = await supabase
      .from('tech_profiles').select('*').eq('open_to_work', true);
    setTechs((data || []).map(normalizeTech));
    setTechsLoading(false);
  }, []);

  // ── Fetch My Contacts (unlocked techs) ──
  const fetchMyContacts = useCallback(async () => {
    if (!employerProfileId) return;
    setMyContactsLoading(true);
    // tech_profile_id IS NOT NULL filters out retained tombstones. Migration
    // 079 keeps the unlock row after a tech deletes their profile — with the
    // snapshot anonymized and an expiry stamped — so the record survives for
    // dispute purposes. Employers should not see those as contacts.
    const { data } = await supabase
      .from('contact_unlocks')
      .select('id, unlocked_at, tech_profile_id, tech_profiles(*)')
      .eq('employer_id', employerProfileId)
      .not('tech_profile_id', 'is', null)
      .order('unlocked_at', { ascending: false });
    setMyContacts((data || []).map(row => ({
      ...row,
      tech: row.tech_profiles ? normalizeTech(row.tech_profiles) : null,
    })));
    setMyContactsLoading(false);
  }, [employerProfileId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { if (tab === 'contacts') { fetchContacts(); fetchConvos(); } }, [tab, fetchContacts, fetchConvos]);
  // Fetch employer's published jobs once profile is known (needed by Invite to Apply on both tabs)
  useEffect(() => {
    if (!employerProfileId) return;
    supabase.from('job_posts').select('*').eq('user_id', user.id).eq('status', 'published')
      .then(({ data }) => setEmployerJobs((data || []).map(normalizeJob)));
  }, [employerProfileId, user.id]);
  useEffect(() => {
    if (tab === 'techs') {
      fetchTechs();
    }
  }, [tab, fetchTechs]);
  useEffect(() => { if (tab === 'mycontacts') fetchMyContacts(); }, [tab, fetchMyContacts]);

  // Fetch employer unread message count
  const fetchEmpUnreadMsgCount = useCallback(async () => {
    if (!employerProfileId) return;
    const { count } = await supabase.from('messages').select('id', { count: 'exact', head: true })
      .eq('employer_profile_id', employerProfileId).eq('sender_type', 'tech').eq('is_read', false);
    setEmpUnreadCount(count || 0);
  }, [employerProfileId]);

  useEffect(() => { fetchEmpUnreadMsgCount(); }, [fetchEmpUnreadMsgCount]);

  // Fetch unread applications count for this employer's jobs
  const fetchUnreadAppsCount = useCallback(async () => {
    if (!user?.id) return;
    const { data: myPosts } = await supabase.from('job_posts').select('id').eq('user_id', user.id);
    if (!myPosts || myPosts.length === 0) { setUnreadAppsCount(0); return; }
    const jobIds = myPosts.map(p => p.id);
    const { count, error } = await supabase.from('applications').select('id', { count: 'exact', head: true })
      .in('job_id', jobIds).eq('is_read', false);
    if (error) { console.error('fetchUnreadAppsCount error:', error); return; }
    setUnreadAppsCount(count || 0);
  }, [user?.id]);

  useEffect(() => { fetchUnreadAppsCount(); }, [fetchUnreadAppsCount]);

  // Poll all badge counts every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadAppsCount();
      fetchEmpUnreadMsgCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadAppsCount, fetchEmpUnreadMsgCount]);

  const togglePost = (postId) => {
    if (expandedPost === postId) { setExpandedPost(null); }
    else { setExpandedPost(postId); if (!applications[postId]) fetchApplications(postId); }
  };

  const handleAppStatus = async (app, postId, newStatus) => {
    await supabase.from('applications').update({ status: newStatus }).eq('id', app.id);
    setApplications(p => ({
      ...p, [postId]: (p[postId] || []).map(a => a.id === app.id ? { ...a, status: newStatus } : a),
    }));

    const post = posts.find(p => p.id === postId);
    const companyName = employerCompanyName || post?.company || 'The employer';
    const jobTitle = post?.title || 'the position';

    if (newStatus === 'hired' && app.tech_profile_id && employerProfileId) {
      // In-app message
      await supabase.from('messages').insert({
        employer_profile_id: employerProfileId,
        tech_profile_id: app.tech_profile_id,
        sender_type: 'employer',
        message: `[ACCEPTED] ${companyName} has accepted your application for ${jobTitle}. Check your messages for next steps.`,
      });
      // Email notification (fire and forget)
      supabase.functions.invoke('send-application-status-notification', {
        body: { tech_profile_id: app.tech_profile_id, employer_profile_id: employerProfileId, job_post_id: postId, status: 'hired' },
      }).catch(() => {});
      // Show unlock prompt if tech is not yet unlocked
      if (!unlockedTechIds.has(app.tech_profile_id)) {
        setAcceptUnlockPrompt({ techId: app.tech_profile_id, techName: app.applicant_name });
      }
    } else if (newStatus === 'under_review' && app.tech_profile_id && employerProfileId) {
      // In-app message for reviewing status
      await supabase.from('messages').insert({
        employer_profile_id: employerProfileId,
        tech_profile_id: app.tech_profile_id,
        sender_type: 'employer',
        message: `${companyName} is reviewing your application for ${jobTitle}.`,
      });
    } else if (newStatus === 'rejected' && app.tech_profile_id && employerProfileId) {
      // In-app message only (no email for rejection)
      await supabase.from('messages').insert({
        employer_profile_id: employerProfileId,
        tech_profile_id: app.tech_profile_id,
        sender_type: 'employer',
        message: `${companyName} has reviewed your application for ${jobTitle} and decided to move forward with other candidates.`,
      });
    }
  };

  const handleDeletePost = async (postId) => {
    await supabase.from('job_posts').delete().eq('id', postId);
    setPosts(p => p.filter(j => j.id !== postId));
    setDeleteConfirm(null);
    if (expandedPost === postId) setExpandedPost(null);
  };

  // ── Proxy reply to applicant (no unlock required, uses messages table) ──
  const handleSendReply = async (app) => {
    if (!replyMessage.trim() || !app.tech_profile_id || !employerProfileId) return;
    setReplySending(true);
    await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: app.tech_profile_id,
      sender_type: 'employer',
      message: replyMessage.trim(),
    });
    // Notify tech via email (fire and forget)
    supabase.functions.invoke('send-message-notification', {
      body: { tech_profile_id: app.tech_profile_id, employer_profile_id: employerProfileId },
    }).catch(() => {});
    setReplySending(false);
    setReplySent(prev => new Set([...prev, app.id]));
    setReplyingTo(null);
    setReplyMessage('');
  };

  // ── Send message from My Contacts tab (uses new messages table) ──
  const handleSendContactMsg = async (contact) => {
    if (!contactMsgText.trim() || !contact.tech_profile_id || !employerProfileId) return;
    setContactMsgSending(true);
    await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: contact.tech_profile_id,
      sender_type: 'employer',
      message: contactMsgText.trim(),
    });
    // Notify tech via email (fire and forget)
    supabase.functions.invoke('send-message-notification', {
      body: { tech_profile_id: contact.tech_profile_id, employer_profile_id: employerProfileId },
    }).catch(() => {});
    setContactMsgSending(false);
    setContactMsgSent(prev => new Set([...prev, contact.id]));
    setContactMsgTo(null);
    setContactMsgText('');
  };

  // ── Invite to Apply ──
  const openInviteModal = (tech) => {
    const availableJobs = employerJobs.filter(j =>
      !existingInvites.has(`${tech.id}-${j.id}`) && !inviteSent.has(`${tech.id}-${j.id}`)
    );
    if (availableJobs.length === 0) return;
    setInviteTarget({ techId: tech.id, techName: tech.name, techRolePref: tech.jobRolePreference, techRoles: tech.specificRoles || [] });
    if (availableJobs.length === 1) {
      setInviteJobId(availableJobs[0].id);
      // Check role mismatch
      checkRoleWarning(tech, availableJobs[0]);
    } else {
      setInviteJobId(null);
      setInviteWarning(false);
    }
  };

  const checkRoleWarning = (tech, job) => {
    if (tech.jobRolePreference === 'specific' && (tech.specificRoles || []).length > 0) {
      const title = (job.title || '').toLowerCase();
      const matches = (tech.specificRoles || []).some(r => {
        const role = r.toLowerCase();
        return title.includes(role) || role.includes(title);
      });
      setInviteWarning(!matches);
    } else {
      setInviteWarning(false);
    }
  };

  const handleSelectInviteJob = (jobId) => {
    setInviteJobId(jobId);
    if (inviteTarget) {
      const job = employerJobs.find(j => j.id === jobId);
      if (job) checkRoleWarning({ jobRolePreference: inviteTarget.techRolePref, specificRoles: inviteTarget.techRoles }, job);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteTarget || !inviteJobId || !employerProfileId) return;
    setInviteSending(true);
    const job = employerJobs.find(j => j.id === inviteJobId);
    const jobTitle = job?.title || 'a position';
    const jobLocation = job?.location || '';

    // Upsert invitation record (re-invite resets a previously declined invitation to pending)
    await supabase.from('invitations').upsert({
      employer_profile_id: employerProfileId,
      tech_profile_id: inviteTarget.techId,
      job_post_id: inviteJobId,
      status: 'pending',
    }, { onConflict: 'employer_profile_id,tech_profile_id,job_post_id' });

    // Send in-app message (embed job_post_id so tech can navigate to it)
    const msgText = `${employerCompanyName || 'We'} invited you to apply for ${jobTitle}${jobLocation ? ` in ${jobLocation}` : ''}. View the job listing to learn more and apply.`;
    await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: inviteTarget.techId,
      sender_type: 'employer',
      message: `[INVITATION:${inviteJobId}] ${msgText}`,
    });

    // Send email notification (fire and forget)
    supabase.functions.invoke('send-invite-notification', {
      body: { tech_profile_id: inviteTarget.techId, employer_profile_id: employerProfileId, job_post_id: inviteJobId },
    }).catch(() => {});

    const key = `${inviteTarget.techId}-${inviteJobId}`;
    setInviteSent(prev => new Set([...prev, key]));
    setExistingInvites(prev => new Set([...prev, key]));
    setInviteSending(false);
    setInviteTarget(null);
    setInviteJobId(null);
    setInviteWarning(false);
  };

  const getAvailableInviteJobs = (techId) => {
    return employerJobs.filter(j =>
      !existingInvites.has(`${techId}-${j.id}`) && !inviteSent.has(`${techId}-${j.id}`)
    );
  };

  // ── Employer conversation reply ──
  const handleConvoReply = async (techProfileId) => {
    if (!convoReplyText.trim() || !employerProfileId) return;
    setConvoReplySending(true);
    await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: techProfileId,
      sender_type: 'employer',
      message: convoReplyText.trim(),
    });
    // Notify tech via email (fire and forget)
    supabase.functions.invoke('send-message-notification', {
      body: { tech_profile_id: techProfileId, employer_profile_id: employerProfileId },
    }).catch(() => {});
    setConvoReplyText('');
    setConvoReplySending(false);
    fetchConvos();
  };

  const markConvoRead = async (techProfileId) => {
    const unreadIds = convos
      .filter(m => m.tech_profile_id === techProfileId && m.sender_type === 'tech' && !m.is_read)
      .map(m => m.id);
    if (unreadIds.length === 0) return;
    await supabase.from('messages').update({ is_read: true }).in('id', unreadIds);
    setConvos(prev => prev.map(m => unreadIds.includes(m.id) ? { ...m, is_read: true } : m));
    setEmpUnreadCount(prev => Math.max(0, prev - unreadIds.length));
  };

  // ── Edit job post ──
  const handleEditPost = async (postId) => {
    // Fetch full post data (posts list only has summary fields)
    const { data } = await supabase.from('job_posts')
      .select('*').eq('id', postId).eq('user_id', user.id).single();
    if (!data) return;
    setEditingPost(data);
    setEditForm({
      company: data.company || '',
      title: data.title || '',
      location: data.location || '',
      type: data.type || 'Contract',
      salary: data.salary || '',
      description: data.description || '',
      requiredCerts: data.required_certs || [],
      preferredCerts: data.preferred_certs || [],
    });
    setEditStep(1);
    setMaxEditStep(2);
    setEditSuccess(false);
    setEditTitleIsOther(!UXO_ROLE_OPTIONS.includes(data.title || '') && (data.title || '') !== '');
  };

  const handleSaveEdit = async () => {
    if (!editingPost || !editForm) return;
    setEditSaving(true);
    const { error } = await supabase.from('job_posts').update({
      company: editForm.company,
      title: editForm.title,
      location: editForm.location,
      type: editForm.type,
      salary: editForm.salary,
      description: editForm.description,
      required_certs: editForm.requiredCerts,
      preferred_certs: editForm.preferredCerts,
    }).eq('id', editingPost.id).eq('user_id', user.id);
    setEditSaving(false);
    if (!error) {
      setEditingPost(null);
      setEditForm(null);
      setEditSuccess(true);
      fetchPosts(); // refresh the list
      setTimeout(() => setEditSuccess(false), 4000);
    }
  };

  const toggleEditCert = (cert, type) => {
    setEditForm(p => {
      const field = type === 'required' ? 'requiredCerts' : 'preferredCerts';
      const other = type === 'required' ? 'preferredCerts' : 'requiredCerts';
      return {
        ...p,
        [field]: p[field].includes(cert) ? p[field].filter(c => c !== cert) : [...p[field], cert],
        [other]: p[other].filter(c => c !== cert),
      };
    });
    setEditStep2Error('');
  };

  const UXO_POSITION_CERTS = [
    "UXO Technician I", "UXO Technician II", "UXO Technician III",
    "UXO Quality Control", "UXO Safety Officer / UXOSO",
    "SUXOS - Senior UXO Supervisor",
  ];
  const validateEditStep1 = () => {
    const errors = {};
    if (!editForm.title.trim()) errors.title = 'Job title is required';
    if (!editForm.location.trim()) errors.location = 'Location is required';
    setEditStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };
  const validateEditStep2 = () => {
    const hasUxoRole = UXO_POSITION_CERTS.some(c => editForm.requiredCerts.includes(c));
    if (!hasUxoRole) {
      setEditStep2Error('At least one UXO position must be marked as Required');
      return false;
    }
    setEditStep2Error('');
    return true;
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // ── Mask email for application view ──
  const maskEmail = (email) => {
    if (!email) return '••••@••••';
    const [local, domain] = email.split('@');
    if (!domain) return '••••@••••';
    return local.charAt(0) + '••••@' + domain;
  };

  // ── My Contacts search/filter ──
  const filteredMyContacts = useMemo(() => {
    if (!contactSearch.trim()) return myContacts;
    const q = contactSearch.toLowerCase();
    return myContacts.filter(c => {
      if (!c.tech) return false;
      return c.tech.name?.toLowerCase().includes(q)
        || c.tech.dodCerts?.some(cert => cert.toLowerCase().includes(q))
        || c.tech.location?.toLowerCase().includes(q);
    });
  }, [myContacts, contactSearch]);

  // ── Job match calculation ──
  const selectedJob = selectedJobId !== 'all'
    ? employerJobs.find(j => j.id === selectedJobId) || null
    : null;

  const techMatches = useMemo(() => {
    if (employerJobs.length === 0) return {};
    const map = {};
    techs.forEach(tech => {
      if (selectedJob) {
        const match = calcJobMatch(tech, selectedJob);
        map[tech.id] = { best: match, all: [match] };
      } else {
        map[tech.id] = calcBestMatch(tech, employerJobs);
      }
    });
    return map;
  }, [techs, employerJobs, selectedJob]);

  // ── Sort techs ──
  const sortedTechs = useMemo(() => {
    const sorted = [...techs];
    if (techSort === 'mostExperienced') {
      sorted.sort((a, b) => (b.totalFieldHours || 0) - (a.totalFieldHours || 0));
    } else if (techSort === 'recentlyUpdated') {
      sorted.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    } else if (employerJobs.length > 0) {
      // Best Fit with job-based matching
      sorted.sort((a, b) => {
        const ma = techMatches[a.id]?.best;
        const mb = techMatches[b.id]?.best;
        const aReq = ma?.meetsAllRequired ? 1 : 0;
        const bReq = mb?.meetsAllRequired ? 1 : 0;
        if (aReq !== bReq) return bReq - aReq;
        const aPref = ma?.preferredPct || 0;
        const bPref = mb?.preferredPct || 0;
        if (aPref !== bPref) return bPref - aPref;
        const aCerts = (a.dodCerts?.length || 0);
        const bCerts = (b.dodCerts?.length || 0);
        if (aCerts !== bCerts) return bCerts - aCerts;
        return (b.totalFieldHours || 0) - (a.totalFieldHours || 0);
      });
    } else {
      // Fallback: sort by total certs held descending, then field hours
      const certCount = (t) => {
        let count = (t.dodCerts || []).length;
        if (t.hazwoper40) count++;
        if (t.hazwoper8) count++;
        if (t.physicalCurrent) count++;
        if (t.clearance) count++;
        if (t.diveCert) count++;
        if (t.driversLicense) count++;
        if (t.cdl) count++;
        if (t.militaryEod) count++;
        if (t.firstAidCpr) count++;
        return count;
      };
      sorted.sort((a, b) => {
        const diff = certCount(b) - certCount(a);
        return diff !== 0 ? diff : (b.totalFieldHours || 0) - (a.totalFieldHours || 0);
      });
    }
    let result = sorted;
    if (hideExpired) result = result.filter(t => !hasAnyCertExpired(t));
    if (onlyMatching && employerJobs.length > 0) result = result.filter(t => techMatches[t.id]?.best?.meetsAllRequired);
    if (onlyVerified) result = result.filter(t => t.logbookVerificationStatus === 'verified');
    if (onlyLogbook) result = result.filter(t => t.hasLogbook);
    return result;
  }, [techs, techSort, hideExpired, onlyMatching, onlyVerified, onlyLogbook, employerJobs, techMatches]);

  const tabItems = [
    { id: 'posts', label: 'Posts & Applicants', icon: '\uD83D\uDCCB', badge: unreadAppsCount > 0 ? unreadAppsCount : null },
    { id: 'techs', label: 'Browse Techs', icon: '\uD83D\uDC65' },
    { id: 'mycontacts', label: 'My Contacts', icon: '\uD83D\uDD13' },
    { id: 'contacts', label: 'Messages', icon: '\uD83D\uDCE8', badge: empUnreadCount > 0 ? empUnreadCount : null },
    { id: 'account', label: 'Account', icon: '\u2699\uFE0F' },
  ];

  // Helper: get days until next reset
  const daysUntilReset = unlockBalance
    ? Math.max(0, 30 - Math.floor((Date.now() - new Date(unlockBalance.last_reset_at).getTime()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div style={s.root}>
      <Navbar view="employerDashboard" setView={(v) => {
          const pathMap = { jobs: '/', techs: '/techs', postJob: '/post-job' };
          navigate(pathMap[v] || `/${v}`);
        }}
        user={user} navigate={navigate} onSignOut={handleSignOut} accountType="employer" />

      <main style={s.main} data-main-container>
        <div style={s.pageHeader}>
          <h1 style={s.pageTitle} data-page-title>Employer Hub</h1>
          <p style={s.pageSub} data-page-subtitle>Manage your job posts, applicants, and tech outreach.</p>
        </div>

        {/* Tab bar */}
        <div style={s.tabs} className="ed-tab-bar">
          {tabItems.map(t => (
            <button key={t.id}
              className={"ed-tab" + (tab === t.id ? " ed-tab-active" : "")}
              style={tab === t.id ? s.tabActive : s.tab}
              onClick={() => { setTab(t.id); setActiveTech(null); }}
            >
              <span className="ed-tab-icon">{t.icon}</span>
              <span className="ed-tab-label">{t.label}</span>
              {t.badge && <span style={{ background: '#d97706', color: '#0d0f10', fontSize: 10, fontWeight: 'bold', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* ── Overage Confirmation Modal ── */}
        {overageConfirmTech && (
          <div style={s.modalOverlay}>
            <div style={s.modalCard}>
              <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12, color: '#e8e4dc' }}>
                Overage Unlock
              </div>
              <p style={{ color: '#9a9490', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px' }}>
                You have used all {unlockBalance?.monthly_allowance || 10} unlocks this month.
                You can upgrade your plan for more unlocks, or use an overage unlock.
                This will be added to your next invoice.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={s.btnSmall} onClick={() => setOverageConfirmTech(null)}>Cancel</button>
                <button style={s.btnPrimary} onClick={confirmOverage} disabled={unlockLoading}>
                  {unlockLoading ? 'Unlocking...' : 'Yes, Unlock'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ MY POSTS TAB ══ */}
        {tab === 'posts' && !editingPost && (
          <div>
            {jobPostSuccess && (
              <div style={{
                background: '#0a2a1a', border: '1px solid #4ade80', borderRadius: 8,
                padding: '12px 18px', marginBottom: 16, color: '#4ade80', fontSize: 14,
              }}>
                {"\u2713"} Job post published successfully!
              </div>
            )}
            {editSuccess && (
              <div style={{
                background: '#0a2a1a', border: '1px solid #4ade80', borderRadius: 8,
                padding: '12px 18px', marginBottom: 16, color: '#4ade80', fontSize: 14,
              }}>
                {"\u2713"} Job post updated successfully.
              </div>
            )}
            {postsLoading && <p style={s.empty}>Loading your posts...</p>}
            {!postsLoading && posts.length === 0 && (
              <div style={s.emptyCard}>
                <p style={s.emptyTitle}>No job posts yet.</p>
                <p style={{ color: '#9a9490', marginBottom: 16 }}>
                  Post your first role and start receiving applicants.
                </p>
                <button style={s.btnPrimary} onClick={() => navigate('/post-job')}>
                  Post a Job {"\u2192"}
                </button>
              </div>
            )}
            {!postsLoading && posts.map(post => {
              const apps = applications[post.id] || [];
              const expanded = expandedPost === post.id;
              const loading = appsLoading[post.id];
              return (
                <div key={post.id} style={s.postCard}>
                  <div style={s.postRow} className="ed-post-row">
                    <div style={s.postInfo}>
                      <div style={s.postTitle}>{post.title}</div>
                      <div style={s.postMeta}>
                        {post.company} {"\u00B7"} {post.location} {"\u00B7"} {post.type}
                        <span style={{ marginLeft: 10 }}>
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div style={s.postActions} className="ed-post-actions">
                      <StatusBadge status={post.status} meta={JOB_STATUS_META} />
                      <button style={s.btnSmall} onClick={() => togglePost(post.id)}>
                        {expanded ? 'Hide Applicants \u25B2' : `Applicants${apps.length ? ` (${apps.length})` : ''} \u25BC`}
                      </button>
                      <button style={{ ...s.btnSmall, color: '#d97706', borderColor: '#d97706' }}
                        onClick={() => handleEditPost(post.id)}>{"\u270F\uFE0F"} Edit</button>
                      {deleteConfirm === post.id ? (
                        <>
                          <span style={{ color: '#f87171', fontSize: 13 }}>Delete?</span>
                          <button style={{ ...s.btnSmall, color: '#f87171', borderColor: '#f87171' }}
                            onClick={() => handleDeletePost(post.id)}>Yes</button>
                          <button style={s.btnSmall} onClick={() => setDeleteConfirm(null)}>No</button>
                        </>
                      ) : (
                        <button style={{ ...s.btnSmall, color: '#f87171', borderColor: '#f87171' }}
                          onClick={() => setDeleteConfirm(post.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                  {expanded && (
                    <div style={s.appsPanel}>
                      {loading && <p style={s.empty}>Loading applicants...</p>}
                      {!loading && apps.length === 0 && (
                        <p style={s.empty}>No applications yet for this post.</p>
                      )}
                      {!loading && apps.map(app => {
                        const isUnlocked = app.tech_profile_id && unlockedTechIds.has(app.tech_profile_id);
                        const tp = app.tech_profiles;
                        return (
                          <div key={app.id} style={s.appRow}>
                            <div style={s.appInfo}>
                              <div style={s.appName}>{app.applicant_name}</div>
                              {/* CRITICAL: mask email unless unlocked */}
                              <div style={s.appEmail}>
                                {isUnlocked ? app.applicant_email : maskEmail(app.applicant_email)}
                              </div>
                              {app.message && <p style={s.appMessage}>"{app.message}"</p>}
                              <div style={{ fontSize: 11, color: '#7a7570', marginTop: 4 }}>
                                Applied {new Date(app.created_at).toLocaleDateString()}
                              </div>
                              {/* Unlock button for applications */}
                              {isPaid && app.tech_profile_id && !isUnlocked && (
                                <button style={{
                                  ...s.btnSmall, color: '#d97706', borderColor: '#d97706',
                                  marginTop: 8, fontSize: 11,
                                }} onClick={() => handleUnlock(app.tech_profile_id)} disabled={unlockLoading}>
                                  {unlockLoading ? 'Unlocking...' : '\uD83D\uDD13 Unlock profile to get direct contact info'}
                                </button>
                              )}
                              {!isPaid && app.tech_profile_id && (
                                <button style={{
                                  ...s.btnSmall, color: '#d97706', borderColor: '#d97706',
                                  marginTop: 8, fontSize: 11,
                                }} onClick={() => setTab('account')}>
                                  {"\uD83D\uDD12"} Subscribe to access contact info
                                </button>
                              )}
                              {/* Unlocked: show badge + contact info */}
                              {isUnlocked && (
                                <div style={{ marginTop: 10 }}>
                                  <span style={{
                                    display: 'inline-block', fontSize: 11,
                                    color: '#4ade80', background: '#0a2a1a', padding: '2px 8px',
                                    borderRadius: 10, border: '1px solid #4ade8033',
                                  }}>
                                    {"\u2713"} Unlocked
                                  </span>
                                  <div style={{
                                    background: '#0a2a1a', border: '1px solid #4ade8033', borderRadius: 8,
                                    padding: '12px 14px', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4,
                                  }}>
                                    {(tp?.email || app.applicant_email) && (
                                      <div style={{ fontSize: 13, color: '#e8e4dc' }}>
                                        <span style={{ color: '#7a7570', marginRight: 6 }}>Email:</span>
                                        <a href={`mailto:${tp?.email || app.applicant_email}`} style={{ color: '#d97706', textDecoration: 'none' }}>{tp?.email || app.applicant_email}</a>
                                      </div>
                                    )}
                                    {tp?.phone && (
                                      <div style={{ fontSize: 13, color: '#e8e4dc' }}>
                                        <span style={{ color: '#7a7570', marginRight: 6 }}>Phone:</span>
                                        <a href={`tel:${tp.phone}`} style={{ color: '#d97706', textDecoration: 'none' }}>{tp.phone}</a>
                                      </div>
                                    )}
                                    {tp?.location && (
                                      <div style={{ fontSize: 13, color: '#e8e4dc' }}>
                                        <span style={{ color: '#7a7570', marginRight: 6 }}>Location:</span>
                                        {tp.location}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Message actions */}
                              {app.tech_profile_id && !replySent.has(app.id) && replyingTo !== app.id && (
                                <button style={{
                                  ...s.btnSmall, color: isUnlocked ? '#d97706' : '#9a9490',
                                  borderColor: isUnlocked ? '#d97706' : undefined,
                                  marginTop: 8, fontSize: 11,
                                }} onClick={() => { setReplyingTo(app.id); setReplyMessage(''); }}>
                                  {"\uD83D\uDCE8"} {isUnlocked ? 'Send Message' : 'Reply via Platform'}
                                </button>
                              )}
                              {replySent.has(app.id) && (
                                <span style={{ display: 'inline-block', marginTop: 8, fontSize: 11, color: '#4ade80' }}>
                                  {"\u2713"} Message sent
                                </span>
                              )}
                              {replyingTo === app.id && (
                                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <textarea style={{
                                    background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6,
                                    color: '#e8e4dc', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                                    outline: 'none', width: '100%', boxSizing: 'border-box', minHeight: 60, resize: 'vertical',
                                  }} placeholder="Write a message to this applicant..."
                                    value={replyMessage} onChange={e => setReplyMessage(e.target.value)} />
                                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                    <button style={s.btnSmall} onClick={() => setReplyingTo(null)}>Cancel</button>
                                    <button style={{ ...s.btnSmall, color: '#d97706', borderColor: '#d97706' }}
                                      onClick={() => handleSendReply(app)} disabled={replySending || !replyMessage.trim()}>
                                      {replySending ? 'Sending...' : 'Send'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div style={s.appStatusWrap}>
                              <span style={{
                                fontSize: 11, fontWeight: 'bold', padding: '3px 10px', borderRadius: 10,
                                color: APP_STATUS_COLORS[app.status] || '#9a9490',
                                background: app.status === 'hired' ? '#0a2a1a' : app.status === 'rejected' ? '#2a0a0a' : 'transparent',
                                border: `1px solid ${(APP_STATUS_COLORS[app.status] || '#9a9490')}33`,
                              }}>
                                {APP_STATUS_LABELS[app.status] || app.status}
                              </span>
                              {app.status === 'submitted' && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                  <button style={{ ...s.btnSmall, color: '#fbbf24', borderColor: '#fbbf2433', fontSize: 11 }}
                                    onClick={() => handleAppStatus(app, post.id, 'under_review')}>Reviewing</button>
                                  <button style={{ ...s.btnSmall, color: '#f87171', borderColor: '#f8717133', fontSize: 11 }}
                                    onClick={() => handleAppStatus(app, post.id, 'rejected')}>Reject</button>
                                </div>
                              )}
                              {app.status === 'under_review' && (
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                  <button style={{ ...s.btnSmall, color: '#4ade80', borderColor: '#4ade8033', fontSize: 11 }}
                                    onClick={() => handleAppStatus(app, post.id, 'hired')}>Accept</button>
                                  <button style={{ ...s.btnSmall, color: '#f87171', borderColor: '#f8717133', fontSize: 11 }}
                                    onClick={() => handleAppStatus(app, post.id, 'rejected')}>Reject</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ══ EDIT JOB POST FORM ══ */}
        {tab === 'posts' && editingPost && editForm && (
          <div>
            <button style={s.backBtn} onClick={() => { setEditingPost(null); setEditForm(null); }}>
              {"\u2190"} Back to Posts
            </button>
            <div style={s.techDetailCard}>
              <h2 style={{ fontSize: 20, fontWeight: 'bold', margin: '0 0 20px' }}>
                {"\u270F\uFE0F"} Edit Job Post
              </h2>
              <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                {[1, 2].map(n => {
                  const isCompleted = n < editStep;
                  const isCurrent = n === editStep;
                  const isVisited = n <= maxEditStep;
                  const isClickable = isVisited && !isCurrent;
                  return (
                    <div key={n}
                      onClick={isClickable ? () => setEditStep(n) : undefined}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 13, fontWeight: 'bold',
                        background: isCurrent ? '#d97706' : isCompleted ? '#1a4a2e' : isVisited ? '#1e2022' : '#1e2022',
                        color: isCurrent ? '#0d0f10' : isCompleted ? '#4ade80' : isVisited ? '#9a9490' : '#555',
                        border: isCompleted ? '1px solid #4ade8033' : 'none',
                        cursor: isClickable ? 'pointer' : 'default',
                        opacity: isVisited ? 1 : 0.4,
                      }}>
                      {isCompleted ? "\u2713" : n}
                    </div>
                  );
                })}
              </div>

              {editStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={s.editLabel}>Company Name</label>
                  <input style={s.editInput} value={editForm.company}
                    onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} />
                  <label style={s.editLabel}>Job Title</label>
                  <select style={s.editInput} value={editTitleIsOther ? 'Other' : (UXO_ROLE_OPTIONS.includes(editForm.title) ? editForm.title : '')}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === 'Other') { setEditTitleIsOther(true); setEditForm(f => ({ ...f, title: '' })); }
                      else { setEditTitleIsOther(false); setEditForm(f => ({ ...f, title: v })); }
                      setEditStep1Errors(prev => ({ ...prev, title: undefined }));
                    }}>
                    <option value="" disabled>Select a role...</option>
                    {[...UXO_ROLE_OPTIONS, 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {editTitleIsOther && (
                    <input style={{ ...s.editInput, marginTop: 8 }} placeholder="Enter custom job title"
                      value={editForm.title} onChange={e => { setEditForm(f => ({ ...f, title: e.target.value })); setEditStep1Errors(prev => ({ ...prev, title: undefined })); }} />
                  )}
                  {editStep1Errors.title && <div style={{ color: '#f87171', fontSize: 12, marginTop: 2 }}>{"\u26A0\uFE0F"} {editStep1Errors.title}</div>}
                  <label style={s.editLabel}>Location</label>
                  <input style={s.editInput} value={editForm.location}
                    onChange={e => { setEditForm(f => ({ ...f, location: e.target.value })); setEditStep1Errors(prev => ({ ...prev, location: undefined })); }} />
                  {editStep1Errors.location && <div style={{ color: '#f87171', fontSize: 12, marginTop: 2 }}>{"\u26A0\uFE0F"} {editStep1Errors.location}</div>}
                  <label style={s.editLabel}>Employment Type</label>
                  <select style={s.editInput} value={editForm.type}
                    onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}>
                    <option>Contract</option><option>Full-Time</option>
                    <option>Part-Time</option><option>Temporary</option>
                  </select>
                  <label style={s.editLabel}>Salary / Pay Rate</label>
                  <input style={s.editInput} value={editForm.salary}
                    onChange={e => setEditForm(f => ({ ...f, salary: e.target.value }))}
                    placeholder="e.g. $45-$55/hr" />
                  <label style={s.editLabel}>Job Description</label>
                  <textarea style={{ ...s.editInput, minHeight: 120, resize: 'vertical' }}
                    value={editForm.description}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button style={s.btnSmall} onClick={() => { setEditingPost(null); setEditForm(null); }}>Cancel</button>
                    <button style={s.btnPrimary} onClick={() => { if (validateEditStep1()) { setEditStep(2); setMaxEditStep(m => Math.max(m, 2)); } }}>Next {"\u2192"}</button>
                  </div>
                </div>
              )}

              {editStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ fontSize: 13, color: '#8a8580', margin: '0 0 8px', lineHeight: 1.5 }}>
                    For each qualification, select Required or Preferred — or leave unselected if not relevant.
                  </p>
                  {ALL_CERT_OPTIONS.map(cert => (
                    <div key={cert} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#0d0f10', border: '1px solid #1e2022', borderRadius: 6, padding: '10px 14px',
                    }}>
                      <span style={{ fontSize: 14, color: '#e8e4dc' }}>{cert}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{
                          background: editForm.requiredCerts.includes(cert) ? '#2a0a0a' : '#1a1c1e',
                          border: `1px solid ${editForm.requiredCerts.includes(cert) ? '#8B0000' : '#2a2c2e'}`,
                          color: editForm.requiredCerts.includes(cert) ? '#ff6b6b' : '#6a6660',
                          padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                        }} onClick={() => toggleEditCert(cert, 'required')}>Required</button>
                        <button style={{
                          background: editForm.preferredCerts.includes(cert) ? '#0a1a2a' : '#1a1c1e',
                          border: `1px solid ${editForm.preferredCerts.includes(cert) ? '#1a3a5a' : '#2a2c2e'}`,
                          color: editForm.preferredCerts.includes(cert) ? '#7ab3d4' : '#6a6660',
                          padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                        }} onClick={() => toggleEditCert(cert, 'preferred')}>Preferred</button>
                      </div>
                    </div>
                  ))}
                  {editStep2Error && <div style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>{"\u26A0\uFE0F"} {editStep2Error}</div>}
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 12 }}>
                    <button style={s.btnSmall} onClick={() => setEditStep(1)}>{"\u2190"} Back</button>
                    <button style={s.btnPrimary} onClick={() => { if (validateEditStep2()) handleSaveEdit(); }} disabled={editSaving}>
                      {editSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ BROWSE TECHS TAB ══ */}
        {tab === 'techs' && !activeTech && (
          <div>
            {!verifiedDisclaimerDismissed && (
              <div style={{
                background: '#111316', border: '1px solid #1e2022', borderRadius: 8,
                padding: '12px 16px', marginBottom: 16, display: 'flex',
                alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{"\u2139\uFE0F"}</span>
                <p style={{ fontSize: 12, color: '#9a9490', margin: 0, lineHeight: 1.6, flex: 1 }}>
                  <strong style={{ color: '#e8e4dc' }}>About documentation badges:</strong> UXOHire reviews documents for format and supervisor attestation — we do not verify authenticity. You are responsible for independent verification before hiring.
                </p>
                <button onClick={() => { localStorage.setItem('uxo_verified_disclaimer_dismissed', '1'); setVerifiedDisclaimerDismissed(true); }} style={{
                  background: 'none', border: 'none', color: '#7a7570', cursor: 'pointer',
                  fontSize: 16, padding: 0, flexShrink: 0, fontFamily: 'inherit',
                }}>{"\u2715"}</button>
              </div>
            )}
            {!isPaid && (
              <div style={{
                background: '#1a1408', border: '1px solid #d97706', borderRadius: 8,
                padding: '14px 20px', marginBottom: 20, display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
              }}>
                <div>
                  <div style={{ color: '#d97706', fontWeight: 'bold', fontSize: 14 }}>
                    {"\uD83D\uDD12"} Free Account — Limited Access
                  </div>
                  <div style={{ color: '#9a9490', fontSize: 12, marginTop: 2 }}>
                    Upgrade to view full profiles, contact information, and message technicians directly.
                  </div>
                </div>
                <button style={{
                  background: '#d97706', border: 'none', color: '#0d0f10', padding: '8px 18px',
                  fontSize: 13, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  flexShrink: 0,
                }} onClick={() => setTab('account')}>
                  Upgrade Now
                </button>
              </div>
            )}
            {isPaid && unlockBalance && (
              <div style={{
                background: '#111316', border: '1px solid #222', borderRadius: 8,
                padding: '10px 18px', marginBottom: 20, display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                fontSize: 13,
              }}>
                <span style={{ color: '#9a9490' }}>
                  {"\uD83D\uDD13"} <strong style={{ color: '#e8e4dc' }}>{unlockBalance.current_balance}</strong> unlocks remaining this month
                  {unlockBalance.current_balance === 0 && <span style={{ color: '#d97706' }}> — contact support for additional unlocks</span>}
                </span>
                <span style={{ color: '#7a7570', fontSize: 11 }}>
                  Resets in {daysUntilReset} days
                </span>
              </div>
            )}
            {/* Job filter + cert expiration filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              {employerJobs.length > 0 && (
                <select style={{
                  background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc',
                  padding: '7px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                }} value={selectedJobId} onChange={e => setSelectedJobId(e.target.value)}>
                  <option value="all">Best match across all jobs</option>
                  {employerJobs.map(job => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
              )}
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: 13, color: '#9a9490', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={hideExpired}
                  onChange={e => setHideExpired(e.target.checked)}
                  style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
                />
                Hide expired certs
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: employerJobs.length > 0 ? 'pointer' : 'default',
                fontSize: 13, color: employerJobs.length > 0 ? '#9a9490' : '#555', userSelect: 'none',
              }} title={employerJobs.length === 0 ? 'Post a job to use requirement filtering' : ''}>
                <input
                  type="checkbox"
                  checked={onlyMatching}
                  onChange={e => setOnlyMatching(e.target.checked)}
                  disabled={employerJobs.length === 0}
                  style={{ accentColor: '#16a34a', width: 16, height: 16, cursor: employerJobs.length > 0 ? 'pointer' : 'default' }}
                />
                Only show matching techs
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: 13, color: '#9a9490', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={onlyVerified}
                  onChange={e => setOnlyVerified(e.target.checked)}
                  style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
                />
                Documented hours only
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: 13, color: '#9a9490', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={onlyLogbook}
                  onChange={e => setOnlyLogbook(e.target.checked)}
                  style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
                />
                Logbook available
              </label>
            </div>
            {/* Sort dropdown + count */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ color: '#7a7570', fontSize: 13 }}>
                {sortedTechs.length} technician{sortedTechs.length !== 1 ? 's' : ''} available
              </span>
              <select style={{
                background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc',
                padding: '7px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              }} value={techSort} onChange={e => setTechSort(e.target.value)}>
                <option value="bestFit">Best Fit</option>
                <option value="mostExperienced">Most Experienced</option>
                <option value="recentlyUpdated">Recently Updated</option>
              </select>
            </div>

            {techsLoading && <p style={s.empty}>Loading technicians...</p>}
            {!techsLoading && sortedTechs.length === 0 && (
              <div style={s.emptyCard}>
                <p style={s.emptyTitle}>No technicians available.</p>
                <p style={{ color: '#9a9490' }}>Check back later for available UXO technicians.</p>
              </div>
            )}
            {!techsLoading && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
                {sortedTechs.map(tech => (
                  <TechCard key={tech.id} tech={tech} onClick={() => setActiveTech(tech)}
                    isFreeEmployer={!isPaid} isUnlocked={unlockedTechIds.has(tech.id)}
                    matchData={techMatches[tech.id] || null} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tech detail view within employer dashboard */}
        {tab === 'techs' && activeTech && (
          <div>
            <button style={s.backBtn} onClick={() => setActiveTech(null)}>{"\u2190"} Back to Techs</button>
            <div style={s.techDetailCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 22, fontWeight: 'bold', margin: '0 0 6px' }}>{activeTech.name}</h2>
                  <div style={{ fontSize: 13, color: '#7a7570', marginBottom: 6 }}>
                    {!isPaid && activeTech.location ? activeTech.location.split(',').pop()?.trim() : (
                      unlockedTechIds.has(activeTech.id) ? activeTech.location : (activeTech.location?.split(',').pop()?.trim() || activeTech.location)
                    )}
                    {" \u00B7 "}{activeTech.travel}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(() => {
                      const isVerified = activeTech.logbookVerificationStatus === 'verified';
                      return (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: 12, fontWeight: 'bold', padding: '4px 12px', borderRadius: 20,
                          background: isVerified ? '#1a1408' : '#111316',
                          border: `1px solid ${isVerified ? '#d9770644' : '#333'}`,
                          color: isVerified ? '#d97706' : '#7a7570',
                        }}>
                          {"\u23F1"} {activeTech.uxoHours} UXO hrs
                          {isVerified ? <span>{"\u2713"} Hours Documented</span> : <span style={{ fontWeight: 'normal' }}>(self-reported)</span>}
                        </span>
                      );
                    })()}
                    {(activeTech.logbookVerifiedSupervisoryHours != null || activeTech.supervisoryHours > 0) && (() => {
                      // Status gates the badge, not the stored figure: returning a
                      // logbook for correction leaves the previously documented
                      // hours on the row, and those must not keep reading as
                      // documented while the submission is back with the tech.
                      const isVerified = activeTech.logbookVerifiedSupervisoryHours != null
                        && activeTech.logbookVerificationStatus === 'verified';
                      const hrs = isVerified ? activeTech.logbookVerifiedSupervisoryHours : activeTech.supervisoryHours;
                      return (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: 12, fontWeight: 'bold', padding: '4px 12px', borderRadius: 20,
                          background: isVerified ? '#0a1a1f' : '#111316',
                          border: `1px solid ${isVerified ? '#2dd4bf44' : '#333'}`,
                          color: isVerified ? '#2dd4bf' : '#7a7570',
                        }}>
                          {"\uD83D\uDC54"} {hrs.toLocaleString()} supervisory hrs
                          {isVerified ? <span>{"\u2713"} Supervisory Hours Documented</span> : <span style={{ fontWeight: 'normal' }}>(self-reported)</span>}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ background: '#1a4a2e', color: '#4ade80', fontSize: 11, padding: '3px 10px', borderRadius: 12 }}>{"\u25CF"} Open to Work</div>
                  {isPaid && unlockedTechIds.has(activeTech.id) && (
                    <div style={{ background: '#0a2a1a', color: '#4ade80', fontSize: 11, padding: '3px 10px', borderRadius: 12, border: '1px solid #4ade8033' }}>
                      {"\u2713"} Unlocked
                    </div>
                  )}
                </div>
              </div>

              {/* Employer verification disclaimer */}
              {(activeTech.logbookVerificationStatus === 'verified' || activeTech.qualityCredentialStatus === 'verified') && (
                <div style={{
                  background: '#111316', border: '1px solid #1e2022', borderRadius: 8,
                  padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>{"\u26A0\uFE0F"}</span>
                  <p style={{ fontSize: 11, color: '#7a7570', margin: 0, lineHeight: 1.6 }}>
                    UXOHire reviews documents for format and supervisor attestation — we do not verify authenticity, document legitimacy, or the accuracy of any information. Employers are responsible for independent verification prior to hiring.{" "}
                    <a href="/terms#verification-disclaimer" style={{ color: '#d97706', textDecoration: 'underline' }}>Learn more</a>
                  </p>
                </div>
              )}

              {/* Job match banners */}
              {employerJobs.length > 0 && (() => {
                const matches = selectedJob
                  ? [calcJobMatch(activeTech, selectedJob)]
                  : employerJobs.map(job => calcJobMatch(activeTech, job));
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {matches.map(match => (
                      <div key={match.jobId} style={{
                        borderRadius: 8, padding: '14px 18px',
                        background: match.hasNoRequirements ? '#1e293b' : match.meetsAllRequired ? '#14532d' : '#451a03',
                        border: `2px solid ${match.hasNoRequirements ? '#334155' : match.meetsAllRequired ? '#16a34a' : '#d97706'}`,
                      }}>
                        <div style={{ fontSize: 15, fontWeight: 'bold', color: '#fff', marginBottom: match.hasNoRequirements ? 0 : 6 }}>
                          {match.hasNoRequirements
                            ? `No requirements set for ${match.jobTitle}`
                            : match.meetsAllRequired
                            ? `\u2713 Qualifies for ${match.jobTitle}`
                            : `Missing ${match.missingRequired.length} requirement${match.missingRequired.length !== 1 ? 's' : ''} for ${match.jobTitle}`}
                        </div>
                        {!match.hasNoRequirements && match.required.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: match.preferred.length > 0 ? 6 : 0 }}>
                            {match.required.map(cert => {
                              const has = techHasCert(activeTech, cert);
                              return (
                                <span key={cert} style={{
                                  fontSize: 11, padding: '3px 10px', borderRadius: 4, fontWeight: 600,
                                  background: has ? '#166534' : '#7f1d1d',
                                  color: has ? '#bbf7d0' : '#fecaca',
                                }}>{has ? "\u2713" : "\u2717"} {cert}</span>
                              );
                            })}
                          </div>
                        )}
                        {match.preferred.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, color: match.meetsAllRequired ? '#bbf7d0' : '#fde68a', marginBottom: 4, fontWeight: 'bold' }}>
                              Preferred — {match.preferredPct}% match
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                              {match.preferred.map(cert => {
                                const has = techHasCert(activeTech, cert);
                                return (
                                  <span key={cert} style={{
                                    fontSize: 11, padding: '3px 10px', borderRadius: 4, fontWeight: 600,
                                    background: has ? '#166534' : '#1e293b',
                                    color: has ? '#bbf7d0' : '#94a3b8',
                                  }}>{has ? "\u2713" : "\u2717"} {cert}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Free employer upgrade banner */}
              {!isPaid && (
                <div style={{
                  background: '#1a1408', border: '1px solid #d97706', borderRadius: 8,
                  padding: '16px 20px', marginBottom: 20, textAlign: 'center',
                }}>
                  <div style={{ color: '#d97706', fontWeight: 'bold', fontSize: 15, marginBottom: 6 }}>
                    {"\uD83D\uDD12"} Subscribe to access contact information
                  </div>
                  <div style={{ color: '#9a9490', fontSize: 13, marginBottom: 12 }}>
                    Upgrade to unlock technician profiles and view direct contact information.
                  </div>
                  <button style={{
                    background: '#d97706', border: 'none', color: '#0d0f10', padding: '10px 24px',
                    fontSize: 14, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  }} onClick={() => setTab('account')}>
                    Upgrade Now — from $1,199/mo
                  </button>
                </div>
              )}

              <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Certifications</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {activeTech.dodCerts.map(c => (
                  <span key={c} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 4, color: '#e8e4dc', background: CERT_COLORS[c] || '#333' }}>{c}</span>
                ))}
                {activeTech.hazwoper40 && <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 4, background: '#1a4a2e', color: '#e8e4dc' }}>HAZWOPER 40-Hour</span>}
              </div>

              {/* Summary visible to paid employers */}
              {isPaid && activeTech.summary && (
                <>
                  <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Summary</div>
                  <p style={{ fontSize: 14, color: '#b0aca6', lineHeight: 1.7, margin: '0 0 16px' }}>{activeTech.summary}</p>
                </>
              )}

              {/* ── Contact info section (unlocked vs locked) ── */}
              {isPaid && unlockedTechIds.has(activeTech.id) && (
                <div style={{
                  background: '#0a2a1a', border: '1px solid #4ade8033', borderRadius: 8,
                  padding: '18px 20px', marginTop: 16,
                }}>
                  <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Contact Information</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {activeTech.email && (
                      <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                        <span style={{ color: '#7a7570', marginRight: 8 }}>Email:</span>
                        <a href={`mailto:${activeTech.email}`} style={{ color: '#d97706', textDecoration: 'none' }}>{activeTech.email}</a>
                      </div>
                    )}
                    {activeTech.phone && (
                      <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                        <span style={{ color: '#7a7570', marginRight: 8 }}>Phone:</span>
                        <a href={`tel:${activeTech.phone}`} style={{ color: '#d97706', textDecoration: 'none' }}>{activeTech.phone}</a>
                      </div>
                    )}
                    {activeTech.location && (
                      <div style={{ fontSize: 14, color: '#e8e4dc' }}>
                        <span style={{ color: '#7a7570', marginRight: 8 }}>Location:</span>
                        {activeTech.location}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Invite to Apply button (after unlock, if jobs available) ── */}
              {isPaid && unlockedTechIds.has(activeTech.id) && employerJobs.length > 0 && getAvailableInviteJobs(activeTech.id).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  {activeTech.available === false ? (
                    <div style={{
                      background: '#111316', border: '1px solid #2a2c2e', borderRadius: 6,
                      padding: '10px 20px', textAlign: 'center', fontSize: 13, color: '#7a7570',
                    }}>This technician is not currently open to work</div>
                  ) : (
                    <button style={{
                      background: 'transparent', border: '2px solid #d97706', color: '#d97706',
                      padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 14, fontWeight: 'bold', width: '100%',
                    }} onClick={() => openInviteModal(activeTech)}>
                      {"\uD83D\uDCE9"} Invite to Apply
                    </button>
                  )}
                </div>
              )}

              {/* ── Documents section (after unlock) ── */}
              {isPaid && unlockedTechIds.has(activeTech.id) && (
                <EmployerDocSection tech={activeTech} />
              )}

              {/* Unlock button for paid employers who haven't unlocked yet */}
              {isPaid && !unlockedTechIds.has(activeTech.id) && (
                <div style={{
                  background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8,
                  padding: '20px 24px', marginTop: 16, textAlign: 'center',
                }}>
                  <div style={{ color: '#9a9490', fontSize: 13, marginBottom: 12 }}>
                    Unlock this profile to view direct contact information.
                    {unlockBalance && unlockBalance.current_balance > 0 && (
                      <span style={{ color: '#7a7570' }}> ({unlockBalance.current_balance} unlocks remaining)</span>
                    )}
                    {unlockBalance && unlockBalance.current_balance === 0 && (
                      <span style={{ color: '#d97706' }}> (Overage: $20)</span>
                    )}
                  </div>
                  <button style={{
                    background: '#d97706', border: 'none', color: '#0d0f10', padding: '10px 24px',
                    fontSize: 14, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  }} onClick={() => handleUnlock(activeTech.id)} disabled={unlockLoading}>
                    {unlockLoading ? 'Unlocking...' : '\uD83D\uDD13 Unlock Profile'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ MY CONTACTS TAB ══ */}
        {tab === 'mycontacts' && (
          <div>
            {!isPaid && (
              <div style={s.emptyCard}>
                <p style={s.emptyTitle}>{"\uD83D\uDD12"} Subscribe to unlock contacts</p>
                <p style={{ color: '#9a9490', marginBottom: 16 }}>
                  Upgrade to Employer Pro to unlock technician profiles and build your contacts list.
                </p>
                <button style={s.btnPrimary} onClick={() => setTab('account')}>
                  Upgrade Now
                </button>
              </div>
            )}
            {isPaid && (
              <>
                {/* Search bar */}
                <div style={{ marginBottom: 20 }}>
                  <input
                    style={{
                      background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc',
                      padding: '10px 16px', borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
                      width: '100%', maxWidth: 400, boxSizing: 'border-box', outline: 'none',
                    }}
                    placeholder="Search by name, role, or certification..."
                    value={contactSearch}
                    onChange={e => setContactSearch(e.target.value)}
                  />
                </div>

                {myContactsLoading && <p style={s.empty}>Loading your unlocked contacts...</p>}

                {!myContactsLoading && filteredMyContacts.length === 0 && !contactSearch && (
                  <div style={s.emptyCard}>
                    <p style={s.emptyTitle}>You haven't unlocked any technicians yet.</p>
                    <p style={{ color: '#9a9490', marginBottom: 16 }}>
                      Browse the tech database to find your next hire.
                    </p>
                    <button style={s.btnPrimary} onClick={() => setTab('techs')}>
                      Browse Techs {"\u2192"}
                    </button>
                  </div>
                )}

                {!myContactsLoading && filteredMyContacts.length === 0 && contactSearch && (
                  <p style={s.empty}>No contacts match "{contactSearch}"</p>
                )}

                {!myContactsLoading && filteredMyContacts.map(c => {
                  if (!c.tech) return null;
                  return (
                    <div key={c.id} style={s.contactCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ fontWeight: 'bold', color: '#e8e4dc', fontSize: 16, marginBottom: 4 }}>
                            {c.tech.name}
                          </div>
                          <div style={{ fontSize: 13, color: '#7a7570', marginBottom: 8 }}>
                            {c.tech.location}
                            {c.tech.uxoHours && <span> {"\u00B7"} {c.tech.uxoHours} UXO hrs</span>}
                          </div>
                          {/* Certs */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                            {c.tech.dodCerts.map(cert => (
                              <span key={cert} style={{
                                fontSize: 10, padding: '2px 7px', borderRadius: 4,
                                color: '#e8e4dc', background: CERT_COLORS[cert] || '#333',
                              }}>{cert}</span>
                            ))}
                          </div>
                          {/* Contact info */}
                          <div style={{ fontSize: 13, color: '#e8e4dc' }}>
                            {c.tech.email && (
                              <div style={{ marginBottom: 2 }}>
                                <span style={{ color: '#7a7570' }}>Email: </span>
                                <a href={`mailto:${c.tech.email}`} style={{ color: '#d97706', textDecoration: 'none' }}>{c.tech.email}</a>
                              </div>
                            )}
                            {c.tech.phone && (
                              <div style={{ marginBottom: 2 }}>
                                <span style={{ color: '#7a7570' }}>Phone: </span>
                                <a href={`tel:${c.tech.phone}`} style={{ color: '#d97706', textDecoration: 'none' }}>{c.tech.phone}</a>
                              </div>
                            )}
                          </div>
                          {/* Availability */}
                          <div style={{ marginTop: 6 }}>
                            {c.tech.available ? (
                              <span style={{ fontSize: 11, color: '#4ade80' }}>{"\u25CF"} Open to Work</span>
                            ) : (
                              <span style={{ fontSize: 11, color: '#9a9490' }}>{"\u25CB"} Not Available</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                          <div style={{ fontSize: 11, color: '#7a7570' }}>
                            Unlocked {new Date(c.unlocked_at).toLocaleDateString()}
                          </div>
                          <button
                            style={{ ...s.btnSmall, color: '#d97706', borderColor: '#d97706' }}
                            onClick={() => { setActiveTech(c.tech); setTab('techs'); }}
                          >
                            View Profile
                          </button>
                          {employerJobs.length > 0 && getAvailableInviteJobs(c.tech.id).length > 0 && (
                            c.tech.available === false ? (
                              <span style={{ fontSize: 11, color: '#7a7570', fontStyle: 'italic' }}>Not open to work</span>
                            ) : (
                              <button style={{ ...s.btnSmall, color: '#d97706', borderColor: '#d97706' }}
                                onClick={() => openInviteModal(c.tech)}>
                                {"\uD83D\uDCE9"} Invite to Apply
                              </button>
                            )
                          )}
                          {!contactMsgSent.has(c.id) && contactMsgTo !== c.id && (
                            <button style={s.btnSmall}
                              onClick={() => { setContactMsgTo(c.id); setContactMsgText(''); }}>
                              {"\uD83D\uDCE8"} Send Message
                            </button>
                          )}
                          {contactMsgSent.has(c.id) && (
                            <span style={{ fontSize: 11, color: '#4ade80' }}>{"\u2713"} Sent</span>
                          )}
                        </div>
                      </div>
                      {contactMsgTo === c.id && (
                        <div style={{ marginTop: 12, borderTop: '1px solid #1e2022', paddingTop: 12 }}>
                          <textarea style={{
                            background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6,
                            color: '#e8e4dc', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                            outline: 'none', width: '100%', boxSizing: 'border-box', minHeight: 60, resize: 'vertical',
                          }} placeholder={`Message ${c.tech.name}...`}
                            value={contactMsgText} onChange={e => setContactMsgText(e.target.value)} />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button style={s.btnSmall} onClick={() => setContactMsgTo(null)}>Cancel</button>
                            <button style={{ ...s.btnSmall, color: '#d97706', borderColor: '#d97706' }}
                              onClick={() => handleSendContactMsg(c)} disabled={contactMsgSending || !contactMsgText.trim()}>
                              {contactMsgSending ? 'Sending...' : 'Send'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* ══ MESSAGES TAB (threaded conversations) ══ */}
        {tab === 'contacts' && (
          <div>
            {/* Search bar */}
            {!openConvo && (
              <div style={{ marginBottom: 16 }}>
                <input style={{
                  background: '#161819', border: '1px solid #2a2c2e', color: '#e8e4dc',
                  padding: '9px 14px', borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
                  width: '100%', maxWidth: 360, boxSizing: 'border-box', outline: 'none',
                }} placeholder="Search conversations by tech name..." value={convoSearch} onChange={e => setConvoSearch(e.target.value)} />
              </div>
            )}

            {convosLoading && <p style={s.empty}>Loading conversations...</p>}

            {/* Thread list */}
            {!convosLoading && !openConvo && (() => {
              const threads = {};
              convos.forEach(m => {
                const tpId = m.tech_profile_id;
                if (!threads[tpId]) {
                  threads[tpId] = {
                    techProfileId: tpId,
                    techName: m.tech_profiles?.name || 'Technician',
                    techLocation: m.tech_profiles?.location || '',
                    messages: [],
                    hasUnread: false,
                    lastDate: m.created_at,
                  };
                }
                threads[tpId].messages.push(m);
                if (m.sender_type === 'tech' && !m.is_read) threads[tpId].hasUnread = true;
              });
              let threadList = Object.values(threads).sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate));
              if (convoSearch.trim()) {
                const q = convoSearch.toLowerCase();
                threadList = threadList.filter(t => t.techName.toLowerCase().includes(q));
              }
              if (threadList.length === 0) {
                return (
                  <div style={s.emptyCard}>
                    <p style={s.emptyTitle}>{convoSearch ? 'No matching conversations' : 'No conversations yet.'}</p>
                    <p style={{ color: '#9a9490' }}>
                      {convoSearch ? `No conversations with "${convoSearch}"` : 'When you message a technician, your conversations appear here.'}
                    </p>
                  </div>
                );
              }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {threadList.map(thread => {
                    const lastMsg = thread.messages[0];
                    return (
                      <div key={thread.techProfileId} style={{
                        ...s.contactCard, cursor: 'pointer',
                        borderLeft: thread.hasUnread ? '3px solid #d97706' : '3px solid transparent',
                      }} onClick={() => { setOpenConvo(thread.techProfileId); markConvoRead(thread.techProfileId); }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
                          <div>
                            <div style={s.contactTechName}>{thread.techName}</div>
                            {thread.techLocation && <div style={{ fontSize: 12, color: '#7a7570' }}>{thread.techLocation}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            {thread.hasUnread && <span style={{ background: '#1a1408', color: '#d97706', fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold', border: '1px solid #d9770633' }}>New</span>}
                            <span style={{ fontSize: 11, color: '#7a7570' }}>{new Date(thread.lastDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <p style={{ color: '#9a9490', fontSize: 13, margin: '4px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lastMsg.message.startsWith('[INVITATION') && (
                            <span style={{ color: '#d97706', fontWeight: 'bold' }}>Invitation: </span>
                          )}
                          {lastMsg.sender_type === 'employer' ? 'You: ' : ''}{lastMsg.message.replace(/^\[INVITATION(?::[^\]]+)?\]\s*/, '')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Open conversation thread */}
            {!convosLoading && openConvo && (() => {
              const threadMsgs = convos.filter(m => m.tech_profile_id === openConvo).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
              const techName = threadMsgs[0]?.tech_profiles?.name || 'Technician';
              return (
                <div>
                  <button style={s.backBtn} onClick={() => { setOpenConvo(null); setConvoReplyText(''); }}>{"\u2190"} Back to Conversations</button>
                  <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>{techName}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                    {threadMsgs.map(msg => {
                      const isInvite = msg.message.startsWith('[INVITATION');
                      const displayMsg = isInvite ? msg.message.replace(/^\[INVITATION(?::[^\]]+)?\]\s*/, '') : msg.message;
                      return (
                      <div key={msg.id} style={{
                        background: isInvite ? '#1a1408' : msg.sender_type === 'employer' ? '#0a1a2a' : '#111316',
                        border: `1px solid ${isInvite ? '#d97706' : msg.sender_type === 'employer' ? '#1a3a5a33' : '#1e2022'}`,
                        borderRadius: 10, padding: '12px 16px',
                        marginLeft: msg.sender_type === 'employer' ? 40 : 0,
                        marginRight: msg.sender_type === 'employer' ? 0 : 40,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 'bold', color: msg.sender_type === 'employer' ? '#60a5fa' : '#d97706' }}>
                            {msg.sender_type === 'employer' ? 'You' : techName}
                          </span>
                          <span style={{ fontSize: 11, color: '#7a7570' }}>{new Date(msg.created_at).toLocaleString()}</span>
                        </div>
                        {isInvite && <span style={{ color: '#d97706', fontWeight: 'bold', fontSize: 12 }}>Invitation</span>}
                        <p style={{ color: '#b0aca6', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{displayMsg}</p>
                      </div>
                      );
                    })}
                  </div>
                  <div style={{ background: '#111316', border: '1px solid #222', borderRadius: 10, padding: '16px' }}>
                    <textarea style={{
                      background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6, color: '#e8e4dc',
                      padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none',
                      width: '100%', boxSizing: 'border-box', minHeight: 70, resize: 'vertical',
                    }} placeholder="Write a reply..." value={convoReplyText} onChange={e => setConvoReplyText(e.target.value)} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button style={s.btnPrimary} onClick={() => handleConvoReply(openConvo)} disabled={convoReplySending || !convoReplyText.trim()}>
                        {convoReplySending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ ACCOUNT TAB ══ */}
        {tab === 'account' && (
          <div>
            <div style={s.accountCard}>
              <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Account Status</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{
                  background: isPaid ? '#0a2a1a' : '#2a1e08',
                  color: isPaid ? '#4ade80' : '#fbbf24',
                  fontSize: 13, fontWeight: 'bold', padding: '5px 14px', borderRadius: 12,
                  border: `1px solid ${isPaid ? '#4ade8033' : '#fbbf2433'}`,
                }}>
                  {isPaid ? '\u2713 Paid Employer' : 'Free Account'}
                </span>
                <span style={{ color: '#7a7570', fontSize: 13 }}>{user.email}</span>
              </div>

              {!isPaid && (
                <div style={{
                  background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '20px',
                }}>
                  <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>Choose Your Plan</div>
                  <div style={{ color: '#9a9490', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
                    Unlock full access to technician profiles, contact information, and direct messaging.
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    {/* Starter */}
                    <div style={{ flex: '1 1 200px', background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8, padding: '16px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: 15, color: '#e8e4dc', marginBottom: 4 }}>Starter</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#d97706', marginBottom: 2 }}>$1,199<span style={{ fontSize: 13, color: '#7a7570', fontWeight: 'normal' }}>/mo</span></div>
                      <div style={{ color: '#7a7570', fontSize: 12, marginBottom: 12 }}>or $11,990/year</div>
                      <ul style={{ color: '#9a9490', fontSize: 13, lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
                        <li>5 active job posts</li>
                        <li>10 unlocks/month (rollover max 15)</li>
                        <li>Email support (2 business days)</li>
                      </ul>
                    </div>
                    {/* Pro */}
                    <div style={{ flex: '1 1 200px', background: '#111316', border: '1px solid #d97706', borderRadius: 8, padding: '16px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: 15, color: '#e8e4dc', marginBottom: 4 }}>Pro</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', color: '#d97706', marginBottom: 2 }}>$1,999<span style={{ fontSize: 13, color: '#7a7570', fontWeight: 'normal' }}>/mo</span></div>
                      <div style={{ color: '#7a7570', fontSize: 12, marginBottom: 12 }}>or $19,990/year</div>
                      <ul style={{ color: '#9a9490', fontSize: 13, lineHeight: 1.8, paddingLeft: 18, margin: 0 }}>
                        <li>Unlimited job posts</li>
                        <li>15 unlocks/month (rollover max 25)</li>
                        <li>Priority email support (1 business day)</li>
                        <li>Monthly account check-in</li>
                      </ul>
                    </div>
                  </div>
                  <button style={{
                    background: '#d97706', border: 'none', color: '#0d0f10', padding: '12px 28px',
                    fontSize: 15, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  }} onClick={() => window.open('mailto:support@uxohire.com?subject=Employer%20Subscription&body=I%20would%20like%20to%20subscribe%20to%20UXOHire.%20Please%20send%20me%20details.', '_blank')}>
                    Contact Us to Subscribe
                  </button>
                  <p style={{ color: '#7a7570', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                    Email support@uxohire.com to set up your subscription.
                  </p>
                </div>
              )}

              {isPaid && (
                <>
                  <div style={{
                    background: '#0a2a1a', border: '1px solid #4ade8033', borderRadius: 8, padding: '20px', marginBottom: 16,
                  }}>
                    <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 16, marginBottom: 8 }}>
                      {"\u2713"} Active Subscription
                    </div>
                    <div style={{ color: '#9a9490', fontSize: 13, lineHeight: 1.6 }}>
                      Your subscription includes 5 active job posts, 10 contact unlocks per month, and email support with responses within 2 business days.
                    </div>
                  </div>

                  {/* Unlock balance display */}
                  {unlockBalance && (
                    <div style={{
                      background: '#111316', border: '1px solid #222', borderRadius: 8, padding: '20px',
                    }}>
                      <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Contact Unlocks</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                        <span style={{
                          fontSize: 32, fontWeight: 'bold',
                          color: unlockBalance.current_balance > 0 ? '#e8e4dc' : '#f87171',
                        }}>
                          {unlockBalance.current_balance}
                        </span>
                        <span style={{ color: '#7a7570', fontSize: 14 }}>
                          unlocks remaining this month
                        </span>
                      </div>
                      <div style={{ color: '#7a7570', fontSize: 13, marginBottom: 4 }}>
                        Resets in {daysUntilReset} days on your billing date
                      </div>
                      {unlockBalance.current_balance === 0 && (
                        <div style={{ color: '#d97706', fontSize: 13, marginTop: 8 }}>
                          0 unlocks remaining — contact support for additional unlocks
                        </div>
                      )}
                      <div style={{ color: '#7a7570', fontSize: 12, marginTop: 8, borderTop: '1px solid #222', paddingTop: 8 }}>
                        Monthly allowance: {unlockBalance.monthly_allowance} {"\u00B7"} Unused unlocks roll over (max 15)
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{
              marginTop: 24, paddingTop: 20, borderTop: '1px solid #222',
            }}>
              <button style={{
                background: 'transparent', border: '1px solid #444', color: '#d97706',
                padding: '10px 20px', fontSize: 14, borderRadius: 6, cursor: 'pointer',
                fontFamily: 'inherit', fontWeight: 'bold',
              }} onClick={() => navigate('/support')}>
                Contact Support
              </button>
              <p style={{ color: '#7a7570', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                Having an issue? Our support team typically responds within 1 business day.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* ── Unlock Prompt After Acceptance ── */}
      {acceptUnlockPrompt && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
        }} onClick={() => setAcceptUnlockPrompt(null)}>
          <div style={{
            background: '#161819', border: '1px solid #2a2c2e', borderRadius: 12,
            padding: '24px 28px', maxWidth: 440, width: '100%', textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{"\u2705"}</div>
            <h3 style={{ fontSize: 18, fontWeight: 'bold', color: '#e8e4dc', margin: '0 0 8px' }}>
              Application Accepted!
            </h3>
            <p style={{ color: '#9a9490', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px' }}>
              Unlock <strong style={{ color: '#e8e4dc' }}>{acceptUnlockPrompt.techName}</strong>'s profile to view their contact information and reach out directly.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button style={{
                background: 'transparent', border: '1px solid #333', color: '#9a9490',
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
              }} onClick={() => setAcceptUnlockPrompt(null)}>Maybe Later</button>
              <button style={{
                background: '#d97706', border: 'none', color: '#0d0f10',
                padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: 13, fontWeight: 'bold',
              }} onClick={() => {
                handleUnlock(acceptUnlockPrompt.techId);
                setAcceptUnlockPrompt(null);
              }} disabled={unlockLoading}>
                {"\uD83D\uDD13"} Unlock Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite to Apply Modal ── */}
      {inviteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
        }} onClick={() => { setInviteTarget(null); setInviteJobId(null); setInviteWarning(false); }}>
          <div style={{
            background: '#161819', border: '1px solid #2a2c2e', borderRadius: 12,
            padding: '24px 28px', maxWidth: 440, width: '100%',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 'bold', color: '#e8e4dc', margin: '0 0 16px' }}>
              Invite {inviteTarget.techName} to Apply
            </h3>

            {/* Job selection (if multiple) */}
            {!inviteJobId && (
              <div>
                <p style={{ color: '#9a9490', fontSize: 13, marginBottom: 12 }}>Select a job to invite them to:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {getAvailableInviteJobs(inviteTarget.techId).map(job => {
                    const alreadyApplied = techApplications[inviteTarget.techId]?.has(job.id);
                    return (
                      <button key={job.id} style={{
                        background: alreadyApplied ? '#111316' : '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8,
                        padding: '12px 16px', cursor: alreadyApplied ? 'default' : 'pointer', textAlign: 'left', fontFamily: 'inherit',
                        opacity: alreadyApplied ? 0.5 : 1,
                      }} onClick={() => { if (!alreadyApplied) handleSelectInviteJob(job.id); }} disabled={alreadyApplied}>
                        <div style={{ fontWeight: 'bold', color: '#e8e4dc', fontSize: 14 }}>
                          {job.title}
                          {alreadyApplied && <span style={{ color: '#4ade80', fontSize: 11, marginLeft: 8, fontWeight: 'normal' }}>(Already applied)</span>}
                        </div>
                        <div style={{ color: '#7a7570', fontSize: 12 }}>{job.location} {"\u00B7"} {job.type}</div>
                      </button>
                    );
                  })}
                </div>
                <button style={{
                  background: 'transparent', border: '1px solid #333', color: '#9a9490',
                  padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 13, marginTop: 12, width: '100%',
                }} onClick={() => { setInviteTarget(null); setInviteJobId(null); }}>Cancel</button>
              </div>
            )}

            {/* Confirmation (with optional role warning) */}
            {inviteJobId && (
              <div>
                {inviteWarning && (
                  <div style={{
                    background: '#2a1a08', border: '1px solid #d97706', borderRadius: 8,
                    padding: '12px 16px', marginBottom: 14,
                  }}>
                    <div style={{ color: '#d97706', fontSize: 13, lineHeight: 1.6 }}>
                      {"\u26A0\uFE0F"} {inviteTarget.techName} has indicated they are only seeking specific roles and{' '}
                      <strong>{employerJobs.find(j => j.id === inviteJobId)?.title}</strong> is not among their preferred positions.
                      You can still send the invitation but they may not be interested.
                    </div>
                  </div>
                )}
                {!inviteWarning && (
                  <p style={{ color: '#9a9490', fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
                    Invite <strong style={{ color: '#e8e4dc' }}>{inviteTarget.techName}</strong> to apply for{' '}
                    <strong style={{ color: '#d97706' }}>{employerJobs.find(j => j.id === inviteJobId)?.title}</strong>?
                  </p>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button style={{
                    background: 'transparent', border: '1px solid #333', color: '#9a9490',
                    padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                  }} onClick={() => { setInviteTarget(null); setInviteJobId(null); setInviteWarning(false); }}>Cancel</button>
                  <button style={{
                    background: '#d97706', border: 'none', color: '#0d0f10',
                    padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 13, fontWeight: 'bold',
                  }} onClick={handleSendInvite} disabled={inviteSending}>
                    {inviteSending ? 'Sending...' : (inviteWarning ? 'Send Anyway' : 'Send Invitation')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Employer Document Section (shows docs from both old + new systems) ───────
const DOC_TYPE_LABELS = {
  resume: 'Resume',
  hazwoper_8hr: 'HAZWOPER 8-Hour Refresher Cert',
  hazwoper_40hr: 'HAZWOPER 40-Hour Cert',
  uxo_eod_cert: 'UXO/EOD Certification',
  physical: 'Physical Exam Cert',
  first_aid_cpr: 'First Aid/CPR Cert',
  logbook: 'Signed Logbook',
  quality_cert: 'Quality Credential Certificate',
  osha_10_construction: '10-Hour OSHA Construction Certificate',
};

function EmployerDocSection({ tech }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      // Fetch from new tech_documents table
      const { data, error } = await supabase
        .from('tech_documents')
        .select('*')
        .eq('tech_profile_id', tech.id)
        .order('uploaded_at', { ascending: false });
      if (error) console.error('Failed to fetch tech documents:', error);

      const newDocs = (data || []).map(d => ({
        id: d.id,
        label: DOC_TYPE_LABELS[d.document_type] || d.document_type,
        file_path: d.file_path,
        file_name: d.file_name,
        uploaded_at: d.uploaded_at,
      }));

      // Build docs from old profile columns (only if not already in new system)
      const newTypes = new Set((data || []).map(d => d.document_type));
      const oldDocs = [];
      if (tech.resumePath && !newTypes.has('resume')) {
        oldDocs.push({ id: 'old-resume', label: 'Resume', file_path: tech.resumePath, file_name: tech.resumePath.split('/').pop() });
      }
      if (tech.hazwoper8CertPath && !newTypes.has('hazwoper_8hr')) {
        oldDocs.push({ id: 'old-hazwoper8', label: 'HAZWOPER 8-Hour Refresher Cert', file_path: tech.hazwoper8CertPath, file_name: tech.hazwoper8CertPath.split('/').pop() });
      }
      if (tech.physicalCertPath && !newTypes.has('physical')) {
        oldDocs.push({ id: 'old-physical', label: 'Physical Exam Cert', file_path: tech.physicalCertPath, file_name: tech.physicalCertPath.split('/').pop() });
      }
      if (tech.certPaths && tech.certPaths.length > 0) {
        tech.certPaths.forEach((p, i) => {
          oldDocs.push({ id: `old-cert-${i}`, label: 'Certification Document', file_path: p, file_name: p.split('/').pop() });
        });
      }

      setDocs([...newDocs, ...oldDocs]);
      setLoading(false);
    })();
  }, [tech.id, tech.resumePath, tech.hazwoper8CertPath, tech.physicalCertPath, tech.certPaths]);

  const handleDownload = async (doc) => {
    const { data, error } = await supabase.storage
      .from('uxo-uploads')
      .createSignedUrl(doc.file_path, 300);
    if (error) {
      console.error('Signed URL failed:', error);
      const { data: dlData, error: dlError } = await supabase.storage
        .from('uxo-uploads')
        .download(doc.file_path);
      if (dlError) { alert('Unable to download this document.'); return; }
      const url = URL.createObjectURL(dlData);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  return (
    <div style={{
      background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8,
      padding: '18px 20px', marginTop: 16,
    }}>
      <div style={{ fontSize: 11, color: '#6a6660', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Documents</div>
      {loading && <p style={{ color: '#7a7570', fontSize: 13 }}>Loading documents...</p>}
      {!loading && docs.length === 0 && (
        <p style={{ color: '#7a7570', fontSize: 13, margin: 0 }}>
          This technician has not uploaded any documents yet.
        </p>
      )}
      {!loading && docs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(doc => (
            <div key={doc.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#0d0f10', border: '1px solid #1e2022', borderRadius: 6,
              padding: '10px 14px', flexWrap: 'wrap', gap: 8,
            }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 'bold', color: '#e8e4dc' }}>{doc.label}</div>
                <div style={{ fontSize: 11, color: '#7a7570' }}>
                  {doc.uploaded_at ? `Uploaded ${new Date(doc.uploaded_at).toLocaleDateString()}` : doc.file_name}
                </div>
              </div>
              <button onClick={() => handleDownload(doc)} style={{
                background: '#d97706', border: 'none', color: '#0d0f10',
                padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                fontWeight: 'bold', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const s = {
  root: { fontFamily: "'Georgia', 'Times New Roman', serif", background: '#0d0f10', color: '#e8e4dc', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  main: { flex: 1, maxWidth: 1100, margin: '0 auto', padding: '0 24px 60px', width: '100%', boxSizing: 'border-box' },

  pageHeader: { padding: '40px 0 24px' },
  pageTitle:  { fontSize: 28, fontWeight: 'bold', color: '#e8e4dc', margin: 0 },
  pageSub:    { color: '#9a9490', fontSize: 15, marginTop: 8 },

  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #222', marginBottom: 28, overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tab: {
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    color: '#7a7570', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
    fontWeight: 'bold', padding: '10px 18px', marginBottom: -1,
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'none', border: 'none', borderBottom: '2px solid #d97706',
    color: '#d97706', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
    fontWeight: 'bold', padding: '10px 18px', marginBottom: -1,
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
  },

  empty:     { color: '#7a7570', fontSize: 14, padding: '24px 0' },
  emptyCard: { background: '#111316', border: '1px solid #222', borderRadius: 10, padding: '32px 28px', textAlign: 'center', marginTop: 16 },
  emptyTitle: { color: '#e8e4dc', fontWeight: 'bold', fontSize: 16, marginBottom: 8 },

  postCard: { background: '#111316', border: '1px solid #222', borderRadius: 10, marginBottom: 12, overflow: 'hidden' },
  postRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', gap: 16, flexWrap: 'wrap' },
  postInfo: { flex: 1, minWidth: 200 },
  postTitle: { fontSize: 16, fontWeight: 'bold', color: '#e8e4dc', marginBottom: 4 },
  postMeta:  { fontSize: 13, color: '#7a7570' },
  postActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },

  btnSmall: {
    background: 'none', border: '1px solid #333', color: '#9a9490',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
    padding: '5px 12px', borderRadius: 6,
  },
  btnPrimary: {
    background: '#d97706', border: 'none', color: '#0d0f10',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
    padding: '10px 20px', borderRadius: 6, fontWeight: 'bold',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#d97706',
    cursor: 'pointer', fontSize: 14, padding: '0 0 16px', fontFamily: 'inherit',
  },

  appsPanel: { borderTop: '1px solid #1e1e1e', padding: '4px 20px 16px' },
  appRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '14px 0', borderBottom: '1px solid #1a1c1e', gap: 16, flexWrap: 'wrap',
  },
  appInfo:    { flex: 1, minWidth: 180 },
  appName:    { fontWeight: 'bold', color: '#e8e4dc', fontSize: 14, marginBottom: 2 },
  appEmail:   { color: '#7a7570', fontSize: 13, marginBottom: 6 },
  appMessage: { color: '#9a9490', fontSize: 13, fontStyle: 'italic', margin: '6px 0 0', lineHeight: 1.5 },
  appStatusWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 130 },
  statusSelect: {
    background: '#1a1c1e', border: '1px solid #333', color: '#e8e4dc',
    fontFamily: 'inherit', fontSize: 12, padding: '4px 8px', borderRadius: 5,
    cursor: 'pointer',
  },

  contactCard: { background: '#111316', border: '1px solid #222', borderRadius: 10, padding: '18px 20px', marginBottom: 10 },
  contactHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 },
  contactTechName: { fontWeight: 'bold', color: '#e8e4dc', fontSize: 15, marginBottom: 4 },
  contactMeta: { color: '#7a7570', fontSize: 13 },
  contactMessage: { color: '#9a9490', fontSize: 14, fontStyle: 'italic', lineHeight: 1.6, margin: 0 },

  accountCard: { background: '#111316', border: '1px solid #222', borderRadius: 10, padding: '28px 24px' },
  techDetailCard: { background: '#111316', border: '1px solid #222', borderRadius: 12, padding: '28px 32px', maxWidth: 720 },

  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, padding: 24,
  },
  modalCard: {
    background: '#111316', border: '1px solid #333', borderRadius: 12,
    padding: '28px 32px', maxWidth: 440, width: '100%',
  },

  editLabel: {
    fontSize: 13, color: '#8a8580', marginBottom: 2, letterSpacing: '0.04em',
  },
  editInput: {
    background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6,
    color: '#e8e4dc', padding: '10px 14px', fontSize: 15,
    fontFamily: "'Georgia', 'Times New Roman', serif",
    outline: 'none', width: '100%', boxSizing: 'border-box',
  },
};
